import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Navbar.css';

// Client menu (for CLIENT users)
const clientMenu = [
  {
    label: '',
    to: '/dashboard',
    icon: 'fa fa-tachometer',
  },
  {
    label: 'Tickets',
    to: '/client-tickets',
  },
  {
    label: 'Cost of works',
    to: '/cost-of-works',
  },
];

// Guardian menu (for GUARDIAN users)
const guardianMenu = (user) => {
  // Check if guardian is awaiting key collection with signed license
  // Laravel: final-message.blade.php shows "My Tickets" instead of "Property search"
  const isAwaitingKeyCollection = localStorage.getItem('guardian_awaiting_key_collection') === 'true';

  return [
    {
      label: 'Property Application',
      to: '/guardian-dashboard',
    },
    {
      label: 'Have a query?',
      to: '/guardian-queries/create',
      icon: 'fa fa-plus',
      isButton: true,
    },
    {
      label: 'My Queries',
      to: '/guardian-queries',
    },
    // Laravel: final-message.blade.php shows "My Tickets" link for awaiting key collection
    isAwaitingKeyCollection
      ? {
          label: 'My Tickets',
          to: '/tickets',
        }
      : {
          label: 'Property search',
          to: '/spaces/available'
        },
    {
      label: 'My Viewings',
      to: '/guardian-viewings',
    },
    {
      label: 'Payments',
      to: user?.userable_id ? `/guardians/${user.userable_id}/payments` : '/guardian-payments',
    },
    {
      label: 'Email preferences',
      to: '/guardian-email-preferences',
    },
    {
      label: 'Edit my profile',
      to: '/guardian-edit-profile',
    },
  ];
};

// Guardian Manager menu (for GUARDIAN-MANAGER and variants) - Matches Laravel exactly
const guardianManagerMenu = [
  {
    label: 'Add ticket',
    to: '/tickets/create',
    icon: 'fa fa-plus',
    isButton: true,
  },
  {
    label: 'Guardians',
    dropdown: [
      { label: 'List guardians', to: '/guardians' },
      { label: 'Add guardian', to: '/guardians/create' },
      { label: 'Placed after end', to: '/rents/after-date-end' },
      { label: 'Rents', to: '/rents' },
      { label: 'Couple list', to: '/rooms/couples' },
      { label: 'After deadline', to: '/guardians/after-deadline' },
      { label: 'Complaints', to: '/complaints' },
      { label: 'Guardian Queries', to: '/guardian-queries' },
    ],
  },
  {
    label: 'Properties',
    dropdown: [
      { label: 'My Calendar', to: '/property_viewings/user/calendar' },
      { label: 'Viewings', to: '/property_viewings' },
      { label: 'Viewing Report', to: '/property_viewings/report' },
      { label: 'Vacant rooms', to: '/properties/vacant-rooms' },
      { label: 'Active rooms', to: '/properties/rooms-search' },
      { label: 'Available spaces', to: '/spaces/available' },
      { label: 'Inspections', to: '/inspections' },
      { label: 'Monthly Inspections', to: '/properties/monthly-inspections' },
      { label: 'Inspection Routes', to: '/inspection-routes' },
    ],
  },
  {
    label: 'Tickets',
    dropdown: [
      { label: 'Maintenance Calendar', to: '/tickets/calendar' },
      { label: 'My calendar', to: '/tickets/member/calendar' },
    ],
  },
];

// Guardian Manager With Maintenance menu - Properties only
const guardianManagerWithMaintenanceMenu = [
  {
    label: 'Properties',
    dropdown: [
      { label: 'List properties', to: '/properties' },
      { label: 'Inspections', to: '/inspections' },
      { label: 'Monthly Inspections', to: '/properties/monthly-inspections' },
    ],
  },
];

// Sales menu (for SALES users) - Only Payments, Properties, Clients, Tickets, Quality
const salesMenu = [
  {
    label: 'Add ticket',
    to: '/tickets/create',
    icon: 'fa fa-plus',
    isButton: true,
  },
  {
    label: 'Payments',
    to: '/payments',
  },
  {
    label: 'Properties',
    dropdown: [
      { label: 'My Calendar', to: '/property_viewings/user/calendar' },
      { label: 'Viewings', to: '/property_viewings' },
      { label: 'Viewing Report', to: '/property_viewings/report' },
      { label: 'Vacant rooms', to: '/properties/vacant-rooms' },
      { label: 'Active rooms', to: '/properties/rooms-search' },
      { label: 'Available spaces', to: '/spaces/available' },
      { label: 'Notice To Quit', to: '/properties/notice-to-quit' },
      { label: 'Inspections', to: '/inspections' },
      { label: 'Monthly Inspections', to: '/properties/monthly-inspections' },
      { label: 'Inspection Routes', to: '/inspection-routes' },
    ],
  },
  {
    label: 'Clients',
    dropdown: [
      { label: 'Contacts', to: '/clients/contacts' },
      { label: 'Certificates', to: '/clients/certificates' },
    ],
  },
  {
    label: 'Tickets',
    dropdown: [
      { label: 'Maintenance Calendar', to: '/tickets/calendar' },
      {
        label: 'My calendar',
        to: `/tickets/member/calendar`,
      },
    ],
  },
  {
    label: 'Quality',
    dropdown: [
      { label: 'Guardians Placed', to: '/quality/guardians/placed' },
      { label: 'Guardians Registered', to: '/guardians/registered' },
      { label: 'Guardians Forecasting', to: '/quality/guardians/forecasting' },
      { label: 'BDM', to: '/bdm/sale' },
    ],
  },
];

// Sales with Guardians menu (for SALES WITH GUARDIANS users)
const salesWithGuardiansMenu = [
  {
    label: 'Add ticket',
    to: '/tickets/create',
    icon: 'fa fa-plus',
    isButton: true,
  },
  {
    label: 'Guardians',
    dropdown: [
      { label: 'List guardians', to: '/guardians' },
      { label: 'Notice To Quit', to: '/rents/notice_to_quit' },
      { label: 'Complaints', to: '/complaints' },
      { label: 'Guardian Queries', to: '/guardian-queries' },
    ],
  },
  {
    label: 'Properties',
    dropdown: [
      { label: 'My Calendar', to: '/property_viewings/user/calendar' },
      { label: 'Viewings', to: '/property_viewings' },
      { label: 'Viewing Report', to: '/property_viewings/report' },
      { label: 'Vacant rooms', to: '/properties/vacant-rooms' },
      { label: 'Active rooms', to: '/properties/rooms-search' },
      { label: 'Available spaces', to: '/spaces/available' },
      { label: 'Notice To Quit', to: '/properties/notice-to-quit' },
      { label: 'Inspections', to: '/inspections' },
      { label: 'Monthly Inspections', to: '/properties/monthly-inspections' },
      { label: 'Inspection Routes', to: '/inspection-routes' },
    ],
  },
  {
    label: 'Clients',
    dropdown: [
      { label: 'Contacts', to: '/clients/contacts' },
      { label: 'Certificates', to: '/clients/certificates' },
    ],
  },
  {
    label: 'Tickets',
    dropdown: [
      { label: 'Maintenance Calendar', to: '/tickets/calendar' },
      {
        label: 'My calendar',
        to: `/tickets/member/calendar`,
      },
    ],
  },
  {
    label: 'Quality',
    dropdown: [
      { label: 'Guardians Placed', to: '/quality/guardians/placed' },
      { label: 'Guardians Registered', to: '/guardians/registered' },
      { label: 'Guardians Forecasting', to: '/quality/guardians/forecasting' },
    ],
  },
];

const Navbar = () => {
  const { user, isGuardianManager, isGuardianManagerWithMaintenance } = useAuth();
  // Reviewer nav gate: driven by the per-user `can_review_certificates` flag.
  const canReviewCerts = !!user?.can_review_certificates;
  // PLI nav gate: driven by the per-user `can_review_pli` flag.
  const canReviewPli = !!user?.can_review_pli;
  // Xero bills nav gate: same model, granted from the Users screen. Off by default, so the
  // new screens appear only for people given access rather than everyone at once.
  const canManageXeroBills = !!user?.can_manage_xero_bills;
  const location = useLocation();
  const navigate = useNavigate();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef();
  // State to trigger re-render when guardian status changes
  const [, setMenuRefresh] = useState(0);

  // Listen for guardian status changes to update menu immediately on login
  useEffect(() => {
    const handleGuardianStatusChange = () => {
      setMenuRefresh(prev => prev + 1);
    };
    window.addEventListener('guardianStatusChanged', handleGuardianStatusChange);
    return () => {
      window.removeEventListener('guardianStatusChanged', handleGuardianStatusChange);
    };
  }, []);

  // Generate menu with dynamic user ID
  const getMenuWithUserID = () => {
    return menu.map(item => {
      if (item.label === 'Properties' && item.dropdown) {
        return {
          ...item,
          dropdown: item.dropdown.map(subItem => {
            if (subItem.label === 'My Calendar') {
              return {
                ...subItem,
                to: `/property_viewings/user/${user?.id || 'calendar'}`
              };
            }
            return subItem;
          })
        };
      }
      return item;
    });
  };

  // Define adminMenu inside the component so it has access to user
  const adminMenu = [
    {
      label: 'Add ticket',
      to: '/tickets/create',
      icon: 'fa fa-plus',
      isButton: true,
    },
    {
      label: 'Users',
      dropdown: [
        { label: 'List users', to: '/users' },
        { label: 'Staff', to: '/staff' }, // Updated to point to /staff
        { label: 'Departments', to: '/departments' },
      ],
    },
    {
      label: 'Guardians',
      dropdown: [
        { label: 'List guardians', to: '/guardians' },
        { label: 'Add guardian', to: '/guardians/create' },
        { label: 'Placed after end', to: '/rents/after-date-end' },
        { label: 'Rents', to: '/rents' },
        { label: 'Couple list', to: '/rooms/couples' },
        { label: 'After deadline', to: '/guardians/after-deadline' },
        { label: 'Hear About', to: '/hear_about' },
        { label: 'Notice To Quit', to: '/rents/notice_to_quit' },
        { label: 'Complaints', to: '/complaints' },
        { label: 'Guardian Queries', to: '/guardian-queries' },
      ],
    },
    {
      label: 'Accounts Receivable',
      dropdown: [
        { label: 'Payments', to: '/payments' },
        { label: 'Without due date', to: '/payments/without-due-date' },
      ],
    },
    {
      label: 'Accounts Payable',
      dropdown: [
        { label: 'Expense Account', to: '/finance/expense-account' },
        { label: 'Expenditures', to: '/expenditures' },
        // P2P v1 — supplier bills already in Xero that have not been recorded here yet
        ...(canManageXeroBills
          ? [
              { label: 'Xero bills', to: '/xero-bills' },
            ]
          : []),
        { label: 'Expenditures categories', to: '/expenditures/categories' },
        { label: 'GG Bank Accounts', to: '/bank-accounts' },
        // { label: 'VIP', to: '/vip' },
        { label: 'Petty Cash', to: '/petty-cash' },
        // { label: 'Budgets', to: '/budgets' },
      ],
    },
    {
      label: 'Properties',
      dropdown: [
        { label: 'List properties', to: '/properties' },
        { label: 'Add property', to: '/properties/create' },
        { label: 'My Calendar', to: '/property_viewings/user/calendar' },
        { label: 'Viewings', to: '/property_viewings' },
        { label: 'Viewing Report', to: '/property_viewings/report' },
        { label: 'Vacant rooms', to: '/properties/vacant-rooms' },
        { label: 'Active rooms', to: '/properties/rooms-search' },
        { label: 'Available spaces', to: '/spaces/available' },
        { label: 'Notice To Quit', to: '/properties/notice-to-quit' },
        { label: 'Inspections', to: '/inspections' },
        { label: 'Inspection Types', to: '/inspection-types' },
        { label: 'Monthly Inspections', to: '/properties/monthly-inspections' },
        { label: 'Inspection Routes', to: '/inspection-routes' },
      ],
    },
    {
      label: 'Clients',
      dropdown: [
        { label: 'List clients', to: '/clients' },
        { label: 'Add client', to: '/clients/create' },
        { label: 'Contacts', to: '/clients/contacts' },
        { label: 'Certificates', to: '/clients/certificates' },
        ...(canReviewCerts
          ? [{ label: 'Certificate Review Queue', to: '/certificates/review-queue' }]
          : []),
        { label: 'Lead Sources', to: '/lead-sources' },
        { label: 'Website Leads', to: '/website-leads' },
        { label: 'Market Segments', to: '/market-segments' },
      ],
    },
    {
      label: 'Tickets',
      dropdown: [
        { label: 'List tickets', to: '/tickets' },
        { label: 'Add ticket', to: '/tickets/create' },
        { label: 'Maintenance Calendar', to: '/tickets/calendar' },
        {
          label: 'My calendar',
          to: `/tickets/member/${user?.id || 'calendar'}/calendar`,
        },
        { label: 'Building Set Ups', to: '/building-set-ups' },
      ],
    },
    {
      label: 'Contractors',
      dropdown: [
        { label: 'List contractors', to: '/contractors' },
        { label: 'Add contractor', to: '/contractors/create' },
        { label: 'Contacts', to: '/contractors/contacts' },
        ...(canReviewPli
          ? [
              { label: 'PLI Status Dashboard', to: '/pli-dashboard' },
              { label: 'PLI Review Queue', to: '/pli-review-queue' },
            ]
          : []),
      ],
    },
    {
      label: 'Reports',
      dropdown: [
        { label: 'Reports', to: '/reports' },
        { label: 'Marketing', to: '/marketing' },
      ],
    },
    {
      label: 'Quality',
      dropdown: [
        { label: 'Guardians Placed', to: '/quality/guardians/placed' },
        { label: 'Guardians Registered', to: '/guardians/registered' },
        {
          label: 'Guardians Forecasting',
          to: '/quality/guardians/forecasting',
        },
        // { label: 'BDM', to: '/bdm' },
      ],
    },
    {
      label: 'Menu',
      dropdown: [
        { label: 'Audit log', to: '/audit' },
        { label: 'Services', to: '/services' },
        { label: 'Settings', to: '/settings' },
        { label: 'Settings Email', to: '/settings/email' },
        { label: 'CRM Roles', to: '/roles' },
        { label: 'CRM Permissions', to: '/permissions' },
        { label: 'CRM Roles permissions', to: '/roles_permissions' },
        // {
        //   label: 'Email About Signed Licence Agreement',
        //   to: '/email-recipients/edit',
        // },
      ],
    },
  ];

  // Determine which menu to show based on user role
  const getMenu = () => {
    if (!user) return adminMenu; // Default fallback

    if (user.role === 'CLIENT') {
      return clientMenu;
    }

    if (user.role === 'GUARDIAN') {
      return guardianMenu(user);
    }

    if (user.role === 'SALES') {
      return salesMenu;
    }

    if (user.role === 'SALES WITH GUARDIANS') {
      return salesWithGuardiansMenu;
    }

    // GUARDIAN-MANAGER-WITH-MAINTENANCE: Properties menu only
    if (user.role === 'GUARDIAN-MANAGER-WITH-MAINTENANCE') {
      return guardianManagerWithMaintenanceMenu;
    }

    // GUARDIAN-MANAGER and other variants
    if (
      user.role === 'GUARDIAN-MANAGER' ||
      user.role === 'GUARDIAN-MAINTENANCE-CLIENT' ||
      user.role === 'GUARDIAN-MANAGER-WITH-PROPERTIES-EDIT'
    ) {
      return guardianManagerMenu;
    }

    // For SYSTEM-ADMINISTRATOR, SENIOR-MANAGEMENT, and other roles
    return adminMenu;
  };

  const menu = getMenu();
  const isClient = user && user.role === 'CLIENT';
  const isGuardian = user && user.role === 'GUARDIAN';
  const isGMWithMaintenance = user && isGuardianManagerWithMaintenance();
  const isGM = user && isGuardianManager() && !isGMWithMaintenance;

  // Close dropdowns on outside click or ESC
  useEffect(() => {
    const handleClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDropdown(null);
        setMobileOpen(false);
      }
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setOpenDropdown(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, []);

  const handleDropdown = (idx) => {
    console.log(
      'Dropdown clicked:',
      idx,
      'Current openDropdown:',
      openDropdown
    );
    setOpenDropdown(openDropdown === idx ? null : idx);
  };

  // Close mobile menu when an item is selected
  const closeMobileMenu = () => {
    setMobileOpen(false);
    setOpenDropdown(null);
  };

  // Keyboard navigation for dropdowns
  const handleDropdownKey = (e, idx) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleDropdown(idx);
    }
    if (e.key === 'ArrowDown' && openDropdown === idx) {
      const firstItem = document.querySelector(
        '.dropdown-menu.show .dropdown-item'
      );
      if (firstItem) firstItem.focus();
    }
  };

  return (
    <nav className="navbar-main" ref={navRef}>
      <div className="navbar-container">
        <button
          className={`navbar-hamburger${mobileOpen ? ' open' : ''}`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
          aria-expanded={mobileOpen}
          aria-controls="navbar-list"
        >
          <span className="bar"></span>
          <span className="bar"></span>
          <span className="bar"></span>
        </button>

        {/* Brand/Logo for Guardian Navigation */}
        {isGuardian && (
          <div className="navbar-brand">
            <span className="brand-circle"></span>
          </div>
        )}

        <ul
          className={`navbar-list${mobileOpen ? ' open' : ''}${
            isClient ? ' client-nav' : ''
          }${isGuardian ? ' guardian-nav' : ''}${
            isGM ? ' guardian-manager-nav' : ''
          }${isGMWithMaintenance ? ' guardian-manager-maintenance-nav' : ''
          }`}
          id="navbar-list"
        >
          {getMenuWithUserID().map((item, idx) => (
            <li
              key={item.label}
              className={`navbar-item${item.dropdown ? ' dropdown' : ''} ${
                openDropdown === idx ? 'show' : ''
              }`}
            >
              {item.isButton ? (
                <NavLink
                  to={item.to}
                  className={`btn-add-ticket ${
                    item.to === '/guardian-queries/create' &&
                    location.pathname === '/guardian-queries/create'
                      ? 'active'
                      : item.to !== '/guardian-queries/create' &&
                        location.pathname === item.to
                      ? 'active'
                      : ''
                  }`}
                  onClick={closeMobileMenu}
                  tabIndex={0}
                  aria-label={item.label}
                >
                  <i className="fa fa-plus" style={{ marginRight: 6 }} />{' '}
                  {item.label}
                </NavLink>
              ) : item.dropdown ? (
                <>
                  <span
                    className="navbar-link"
                    aria-haspopup="true"
                    aria-expanded="false"
                    aria-controls={`dropdown-menu-${idx}`}
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      // On mobile, always toggle dropdown instead of navigating
                      const isMobile = window.innerWidth <= 900;
                      if (isMobile) {
                        handleDropdown(idx);
                        return;
                      }
                      let navigationUrl = '';
                      switch (item.label) {
                        case 'Users':
                          navigationUrl = '/users';
                          break;
                        case 'Guardians':
                          navigationUrl = '/guardians';
                          break;
                        case 'Accounts Receivable':
                          navigationUrl = '/payments';
                          break;
                        case 'Accounts Payable':
                          navigationUrl = '/finance/expense-account';
                          break;
                        case 'Properties':
                          navigationUrl = '/properties';
                          break;
                        case 'Clients':
                          navigationUrl = '/clients';
                          break;
                        case 'Tickets':
                          navigationUrl = '/tickets';
                          break;
                        case 'Contractors':
                          navigationUrl = '/contractors';
                          break;
                        case 'Reports':
                          navigationUrl = '/reports';
                          break;
                        case 'Quality':
                          navigationUrl = '/quality/guardians/placed';
                          break;
                        case 'Menu':
                          navigationUrl = '/reports';
                          break;
                        default:
                          handleDropdown(idx);
                          return;
                      }
                      if (navigationUrl) {
                        closeMobileMenu();
                        navigate(navigationUrl);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        // On mobile, always toggle dropdown instead of navigating
                        const isMobile = window.innerWidth <= 900;
                        if (isMobile) {
                          handleDropdown(idx);
                          return;
                        }
                        let navigationUrl = '';
                        switch (item.label) {
                          case 'Users':
                            navigationUrl = '/users';
                            break;
                          case 'Guardians':
                            navigationUrl = '/guardians';
                            break;
                          case 'Accounts Receivable':
                            navigationUrl = '/payments';
                            break;
                          case 'Accounts Payable':
                            navigationUrl = '/finance/expense-account';
                            break;
                          case 'Properties':
                            navigationUrl = '/properties';
                            break;
                          case 'Clients':
                            navigationUrl = '/clients';
                            break;
                          case 'Tickets':
                            navigationUrl = '/tickets';
                            break;
                          case 'Contractors':
                            navigationUrl = '/contractors';
                            break;
                          case 'Reports':
                            navigationUrl = '/reports';
                            break;
                          case 'Quality':
                            navigationUrl = '/quality/guardians/placed';
                            break;
                          case 'Menu':
                            navigationUrl = '/reports';
                            break;
                          default:
                            handleDropdownKey(e, idx);
                            return;
                        }
                        if (navigationUrl) {
                          closeMobileMenu();
                          navigate(navigationUrl);
                        }
                      } else {
                        handleDropdownKey(e, idx);
                      }
                    }}
                  >
                    {item.label} <span className="caret" />
                  </span>
                  <ul
                    className="dropdown-menu"
                    id={`dropdown-menu-${idx}`}
                    role="menu"
                  >
                    {item.dropdown.map((sub) => (
                      <li key={sub.label}>
                        <NavLink to={sub.to} className="dropdown-item" onClick={closeMobileMenu}>
                          {sub.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <NavLink
                  to={item.to}
                  className={`navbar-link ${
                    item.to === '/guardian-queries' &&
                    location.pathname === '/guardian-queries'
                      ? 'active'
                      : item.to !== '/guardian-queries' &&
                        location.pathname === item.to
                      ? 'active'
                      : ''
                  }`}
                  onClick={closeMobileMenu}
                  tabIndex={0}
                >
                  {item.icon && (
                    <i className={item.icon} style={{ marginRight: 6 }} />
                  )}
                  {item.label}
                </NavLink>
              )}
            </li>
          ))}
        </ul>
      </div>
      {/* Teal separator line for Guardian Navigation */}
      {isGuardian && <div className="navbar-separator"></div>}
    </nav>
  );
};

export default Navbar;
